import { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import ClientLayout from "../components/layout/ClientLayout";
import axiosInstance from "../../../utils/axiosInstance";
import { fetchAndSetUser } from "../../../utils/refreshSessionData";

const TYPE_BANK = 1;
const TYPE_USDT = 2;
const TYPE_JAZZ = 4;
const TYPE_EASY = 5;


// ─── Editable input row ───────────────────────────────────────────────────────
function InputRow({ label, icon, children }) {
  return (
    <div className="dw-field-row">
      {icon
        ? <span className="dw-field-icon">{icon}</span>
        : <span className="dw-field-label">{label}</span>
      }
      <span className="dw-field-sep">|</span>
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
const AutoWalletWithdrawal = () => {
  const dispatch = useDispatch();
  const [mainTab,       setMainTab]       = useState("pkr");
  const [pkrSub,        setPkrSub]        = useState(TYPE_EASY);

  // PKR fields
  const [accountTitle,  setAccountTitle]  = useState("");
  const [bankName,      setBankName]      = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [amount,        setAmount]        = useState("");

  // USDT fields
  const [bep20Address,  setBep20Address]  = useState("");
  const [usdtAmt,       setUsdtAmt]       = useState("");


  const [submitting,    setSubmitting]    = useState(false);
  const [message,       setMessage]       = useState({ text: "", isError: false });

  const usdtIcon = (
    <img src="/assets/autoWallet/png/usdt-logo.png"
      alt="USDT" style={{ height: 18, verticalAlign: "middle" }} />
  );

  const resetPkrForm = () => {
    setAccountTitle("");
    setBankName("");
    setAccountNumber("");
    setAmount("");
  };

  const resetUsdtForm = () => {
    setBep20Address("");
    setUsdtAmt("");
  };

  const switchMain = (tab) => {
    setMainTab(tab);
    setMessage({ text: "", isError: false });
  };

  const validateForm = useCallback(() => {
    if (mainTab === "usdt") {
      if (!bep20Address.trim()) return "BEP20 address is required";
      const v = parseFloat(usdtAmt);
      if (!v || v <= 0) return "Please enter a valid USDT amount";
    } else {
      const v = parseFloat(amount);
      if (!v || v <= 0) return "Please enter a valid amount";
      if (!accountNumber.trim()) return "Account number is required";
    }
    return null;
  }, [mainTab, bep20Address, usdtAmt, amount, accountNumber]);

  const handleSubmit = async () => {
    setMessage({ text: "", isError: false });
    const err = validateForm();
    if (err) { setMessage({ text: err, isError: true }); return; }

    const payMode = mainTab === "usdt" ? TYPE_USDT : pkrSub;
    const finalAmount = mainTab === "usdt" ? parseFloat(usdtAmt) : parseFloat(amount);

    setSubmitting(true);
    try {
      const res = await axiosInstance.post("/wallet/withdrawal-request", {
        pay_mode: payMode,
        amount: finalAmount,
        account_title: accountTitle.trim(),
        bank_name: bankName.trim(),
        account_number: accountNumber.trim(),
        bep20_address: bep20Address.trim(),
      });
      if (res.data?.status) {
        setMessage({ text: res.data.message || "Withdrawal request submitted!", isError: false });
        resetPkrForm();
        resetUsdtForm();
        fetchAndSetUser(dispatch);
      } else {
        setMessage({ text: res.data?.message || "Submission failed", isError: true });
      }
    } catch (e) {
      setMessage({ text: e?.response?.data?.message || "Submission failed", isError: true });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ClientLayout showLoader={false}>
      <style>{css}</style>
      <div className="dw-page">
        <div className="dw-page-title">
          Withdrawal
          <Link to="/auto-wallet/my-withdrawals" className="dw-title-link">
            View Requests
          </Link>
        </div>

        <div className="dw-container">
          {/* ── Main tabs ── */}
          <div className="dw-tabs">
            <div
              className={`dw-main-tab${mainTab === "pkr" ? " active" : ""}`}
              onClick={() => switchMain("pkr")}
            >
              <div
                className={`dw-main-tab-inner${
                  mainTab === "pkr" ? " dark" : " light"
                }`}
              >
                <img
                  src="/assets/autoWallet/png/withdrawal_rs.png"
                  alt="UPI"
                  style={{ height: 40, objectFit: "contain" }}
                />
                <div
                  className={`dw-main-tab-label${
                    mainTab === "pkr" ? " light-text" : " dark-text"
                  }`}
                >
                  WITHDRAWAL RS
                </div>
              </div>
            </div>

            <div
              className={`dw-main-tab${mainTab === "usdt" ? " active" : ""}`}
              onClick={() => switchMain("usdt")}
            >
              <div
                className={`dw-main-tab-inner${
                  mainTab === "usdt" ? " dark" : " light"
                }`}
              >
                <img
                  src="/assets/autoWallet/png/usdt-logo.png"
                  alt="USDT"
                  style={{ height: 40, objectFit: "contain" }}
                />
                <div
                  className={`dw-main-tab-label${
                    mainTab === "usdt" ? " light-text" : " dark-text"
                  }`}
                >
                  WITHDRAWAL USDT
                </div>
              </div>
            </div>
          </div>

          {/* ══════════ PKR ══════════ */}
          {mainTab === "pkr" && (
            <div className="dw-card">
              {/* Sub-tabs */}
              <div className="dw-sub-tabs">
                {[
                  {
                    id: TYPE_BANK,
                    content: <span className="dw-sub-text">BANK</span>
                  },
                  {
                    id: TYPE_JAZZ,
                    content: (
                      <img
                        src="/assets/autoWallet/png/jazzcash-logo.png"
                        alt="JazzCash"
                        style={{
                          width: "90%",
                          height: 70,
                          objectFit: "contain"
                        }}
                      />
                    )
                  },
                  {
                    id: TYPE_EASY,
                    content: (
                      <img
                        src="/assets/autoWallet/png/easypaisa2.png"
                        alt="EasyPaisa"
                        style={{
                          width: "90%",
                          height: 70,
                          objectFit: "contain"
                        }}
                      />
                    )
                  }
                ].map(({ id, content }) => (
                  <div
                    key={id}
                    className={`dw-sub-tab${pkrSub === id ? " active" : ""}`}
                    onClick={() => {
                      setPkrSub(id);
                      setAccountTitle("");
                      setBankName("");
                      setAccountNumber("");
                    }}
                  >
                    {content}
                  </div>
                ))}
              </div>

              <div className="dw-section-title">
                <img
                  src="/assets/autoWallet/png/wallet-r-5ca037e5.png"
                  alt=""
                  style={{ height: 20 }}
                />
                WITHDRAWAL RS
              </div>

              {/* Bank fields */}
              {pkrSub === TYPE_BANK && (
                <>
                  <InputRow label="Account Title">
                    <input
                      className="dw-input"
                      type="text"
                      placeholder="Account Title"
                      value={accountTitle}
                      onChange={(e) => setAccountTitle(e.target.value)}
                    />
                  </InputRow>
                  <InputRow label="Bank Name">
                    <input
                      className="dw-input"
                      type="text"
                      placeholder="Bank Name"
                      value={bankName}
                      onChange={(e) => setBankName(e.target.value)}
                    />
                  </InputRow>
                  <InputRow label="Account">
                    <input
                      className="dw-input"
                      type="text"
                      inputMode="numeric"
                      placeholder="Account Number"
                      value={accountNumber}
                      onChange={(e) => setAccountNumber(e.target.value)}
                    />
                  </InputRow>
                </>
              )}

              {/* JazzCash fields */}
              {pkrSub === TYPE_JAZZ && (
                <>
                  <InputRow label="Account Title">
                    <input
                      className="dw-input"
                      type="text"
                      placeholder="Account Title"
                      value={accountTitle}
                      onChange={(e) => setAccountTitle(e.target.value)}
                    />
                  </InputRow>
                  <InputRow label="Account">
                    <input
                      className="dw-input"
                      type="text"
                      inputMode="numeric"
                      placeholder="JazzCash Number"
                      value={accountNumber}
                      onChange={(e) => setAccountNumber(e.target.value)}
                    />
                  </InputRow>
                </>
              )}

              {/* EasyPaisa fields */}
              {pkrSub === TYPE_EASY && (
                <>
                  <InputRow label="Account Title">
                    <input
                      className="dw-input"
                      type="text"
                      placeholder="Account Title"
                      value={accountTitle}
                      onChange={(e) => setAccountTitle(e.target.value)}
                    />
                  </InputRow>
                  <InputRow label="Account">
                    <input
                      className="dw-input"
                      type="text"
                      inputMode="numeric"
                      placeholder="EasyPaisa Number"
                      value={accountNumber}
                      onChange={(e) => setAccountNumber(e.target.value)}
                    />
                  </InputRow>
                </>
              )}

              <div className="dw-section-title" style={{ marginTop: 16 }}>
                <img
                  src="/assets/autoWallet/png/saveWallet-26023150.png"
                  alt=""
                  style={{ height: 20 }}
                />
                WITHDRAWAL AMOUNT
              </div>

              <InputRow label="Amount Rs.">
                <input
                  className="dw-input"
                  type="number"
                  inputMode="numeric"
                  placeholder="Please Enter Amount"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </InputRow>
            </div>
          )}

          {/* ══════════ USDT ══════════ */}
          {mainTab === "usdt" && (
            <div className="dw-card">
              <div className="dw-section-title">
                {usdtIcon} WITHDRAWAL ADDRESS
              </div>

              <InputRow label="BEP20">
                <input
                  className="dw-input"
                  type="text"
                  placeholder="BEP20 Address"
                  value={bep20Address}
                  onChange={(e) => setBep20Address(e.target.value)}
                />
              </InputRow>

              <div className="dw-section-title" style={{ marginTop: 18 }}>
                <img
                  src="/assets/autoWallet/png/saveWallet-26023150.png"
                  alt=""
                  style={{ height: 20 }}
                />
                WITHDRAWAL AMOUNT
              </div>

              <InputRow label="Amount">
                <input
                  className="dw-input"
                  type="number"
                  inputMode="numeric"
                  placeholder="Please Enter Amount"
                  value={usdtAmt}
                  onChange={(e) => setUsdtAmt(e.target.value)}
                />
              </InputRow>
            </div>
          )}

          {message.text && (
            <div className={`dw-msg${message.isError ? " error" : " success"}`}>
              {message.text}
            </div>
          )}

          <button
            className="dw-submit-btn"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? "Please wait..." : "Withdrawal"}
          </button>

          {/* Withdrawal Instructions */}
          <div className="dw-instructions">
            <div className="dw-section-title">
              <img
                src="/assets/autoWallet/png/balance.png"
                alt=""
                style={{ height: 20 }}
              />
              Withdrawal Instructions
            </div>
            <ul
              style={{
                paddingLeft: 18,
                color: "#666",
                fontSize: 13,
                lineHeight: 1.8,
                margin: 0
              }}
            >
              <li>
                Withdraw time:{" "}
                <span style={{ color: "red" }}>00:00 – 23:50</span>
              </li>
              <li>
                Withdrawal amount range:{" "}
                <span style={{ color: "red" }}>Rs.110.00 – Rs.100,000.00</span>
              </li>
              <li>
                Please confirm your account information before withdrawing. If
                your information is incorrect, our company will not be liable
                for any losses.
              </li>
              <li>
                If your account information is incorrect, please contact
                customer service.
              </li>
            </ul>
          </div>
        </div>
      </div>
    </ClientLayout>
  );
};

// ─── CSS (reused from deposit page) ──────────────────────────────────────────
const css = `
  .dw-page {
    background: #eef0f3;
    min-height: 100vh;
    padding-bottom: 50px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  }

  .dw-page-title {
    position: relative;
    text-align: center;
    background: #3d6b8b;
    color: #fff;
    font-size: 17px;
    font-weight: 600;
    padding: 12px 16px;
    letter-spacing: 0.5px;
  }
  .dw-title-link {
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 12px;
    font-weight: 600;
    color: #f0c060;
    text-decoration: none;
    background: rgba(255,255,255,0.12);
    border: 1px solid rgba(255,255,255,0.25);
    border-radius: 20px;
    padding: 4px 10px;
    white-space: nowrap;
    letter-spacing: 0.3px;
    transition: background 0.15s;
  }
  .dw-title-link:hover { background: rgba(255,255,255,0.22); color: #ffe9a0; text-decoration: none; }

  .dw-container { padding: 14px 12px; }

  .dw-tabs { display: flex; gap: 8px; margin-bottom: 14px; }
  .dw-main-tab { flex: 1; cursor: pointer; border-radius: 10px; overflow: hidden; }
  .dw-main-tab-inner {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 12px 8px;
    min-height: 100px;
  }
  .dw-main-tab-inner.light {
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 10px;
  }
  .dw-main-tab-inner.dark {
    background: linear-gradient(160deg, var(--theme-color) 0%, #3d6b8b 100%);
    border-radius: 10px;
  }
  .dw-main-tab-label {
    font-size: 12px;
    font-weight: 700;
    margin-top: 6px;
    text-align: center;
    letter-spacing: 0.3px;
  }
  .dw-main-tab-label.dark-text  { color: #333; }
  .dw-main-tab-label.light-text { color: #fff; }

  .dw-card {
    background: #fff;
    border-radius: 14px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.08);
    padding: 16px;
    margin-bottom: 14px;
  }

  .dw-section-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 700;
    color: #222;
    margin-bottom: 12px;
    text-transform: uppercase;
  }

  .dw-sub-tabs {
    display: flex;
    gap: 6px;
    margin-bottom: 14px;
  }
  .dw-sub-tab {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    background: #f5f5f5;
    border: 1.5px solid #e0e0e0;
    border-radius: 8px;
    cursor: pointer;
    height: 100px;
    overflow: hidden;
    transition: border-color 0.15s, background 0.15s;
  }
  .dw-sub-tab.active {
    background: linear-gradient(160deg, var(--theme-color) 0%, #3d6b8b 100%);
    border-color: var(--theme-color);
  }
  .dw-sub-tab.active .dw-sub-text { color: #fff; }
  .dw-sub-text { font-size: 13px; font-weight: 700; color: #333; padding: 8px 4px; }

  .dw-field-row {
    display: flex;
    align-items: center;
    background: #f5f7f9;
    border: 1px solid #e8eaed;
    border-radius: 8px;
    padding: 10px 12px;
    margin-bottom: 10px;
    min-height: 44px;
  }
  .dw-field-label {
    font-size: 12px;
    font-weight: 600;
    color: #000000;
    white-space: nowrap;
    min-width: 70px;
    flex-shrink: 0;
  }
  .dw-field-icon {
    display: flex;
    align-items: center;
    min-width: 36px;
    flex-shrink: 0;
  }
  .dw-field-sep {
    color: #ccc;
    margin: 0 10px;
    font-size: 18px;
    flex-shrink: 0;
    line-height: 1;
  }
  .dw-input {
    width: 100%;
    border: none;
    outline: none;
    background: transparent;
    font-size: 13px;
    color: #333;
  }
  .dw-input::placeholder { color: #aaa; }

  .dw-chips {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    margin-bottom: 14px;
  }
  .dw-chip {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 10px 4px;
    background: #f0f2f5;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    color: #eb8a1f;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s;
  }
  .dw-chip.active {
    background: #eb8a1f;
    border-color: #eb8a1f;
    color: #fff;
  }
  .dw-chip:hover:not(.active) { background: #e5e8ec; }
  .dw-chip-currency { font-size: 11px; color: #eb8a1f; }
  .dw-chip.active .dw-chip-currency { color: #fff; }

  .dw-msg {
    text-align: center;
    padding: 10px 14px;
    border-radius: 8px;
    margin-bottom: 12px;
    font-size: 14px;
    font-weight: 500;
  }
  .dw-msg.success { background: #e8f9f0; color: #1a8a55; border: 1px solid #b2e8cc; }
  .dw-msg.error   { background: #fef0f0; color: #c0392b; border: 1px solid #f5c0bb; }

  .dw-submit-btn {
    width: 100%;
    padding: 15px;
    background: #02b181;
    color: #fff;
    font-size: 16px;
    font-weight: 700;
    border: none;
    border-radius: 50px;
    cursor: pointer;
    margin-bottom: 18px;
    letter-spacing: 0.5px;
    transition: opacity 0.2s;
    box-shadow: 0 4px 15px rgba(2,177,129,0.35);
  }
  .dw-submit-btn:disabled { opacity: 0.6; cursor: not-allowed; }
  .dw-submit-btn:hover:not(:disabled) { opacity: 0.88; }

  .dw-instructions {
    background: #fff;
    border-radius: 14px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.06);
    padding: 16px;
  }
`;

export default AutoWalletWithdrawal;
